/*
 * Reading the server's own labels.
 *
 * Both functions here parse a string the server already formatted rather than
 * re-deriving the same fact from the value beside it, and that is the whole
 * point of the file: the value is an instant, and interpreting it on the device
 * uses the CUSTOMER's timezone. The label was rendered in the BUSINESS's.
 *
 * A customer travelling, or a phone left on the wrong zone, is not an edge case
 * for a booking app — it is a salon in Bengaluru and a customer whose phone
 * still says London, being shown a Friday slot filed under Thursday.
 */

/** The periods a day's slots are grouped into, in the order they are offered. */
export const PERIODS = ['Morning', 'Afternoon', 'Evening'] as const

export type Period = (typeof PERIODS)[number]

/*
 * A word in a date label — the two shapes a formatted date arrives in.
 *
 * `\p{M}` as well as `\p{L}`, and it is not decoration: in most Indic scripts
 * the vowel signs are COMBINING MARKS rather than letters, so a letters-only
 * class stops at the first matra and the whole label falls through to the null
 * branch. "अगस्त" is four letters and two marks.
 *
 * Today every label is formatted `en-IN` by formatDateLabel in
 * packages/flow/src/time.ts, so this is insurance rather than a live
 * requirement — but it is the difference between a rail that degrades when
 * that locale becomes a tenant setting and one that quietly stops being a rail.
 */
const DAY_FIRST = /^([\p{L}\p{M}]+),?\s+(\d{1,2})\s+([\p{L}\p{M}]+)$/u

/** Some locales put the month first: "Tue, Aug 18". */
const MONTH_FIRST = /^([\p{L}\p{M}]+),?\s+([\p{L}\p{M}]+)\s+(\d{1,2})$/u

/**
 * "Tue, 18 Aug" → weekday / day / month, for the date rail's three lines.
 *
 * Returns null rather than guessing when the shape is unfamiliar, and the
 * caller falls back to printing the label whole — which is always correct, just
 * less pretty.
 */
export function splitDateLabel(label: string): { wd: string; d: string; mo: string } | null {
  const trimmed = label.trim()

  const dayFirst = DAY_FIRST.exec(trimmed)
  if (dayFirst) return { wd: dayFirst[1]!, d: dayFirst[2]!, mo: dayFirst[3]! }

  const monthFirst = MONTH_FIRST.exec(trimmed)
  if (monthFirst) return { wd: monthFirst[1]!, d: monthFirst[3]!, mo: monthFirst[2]! }

  return null
}

/**
 * "09:00 am" → Morning.
 *
 * Anything unparseable is Morning rather than dropped: a slot filed under the
 * wrong heading is still bookable, and a slot in no group at all has vanished
 * from a business's day.
 */
export function periodOf(label: string): Period {
  const match = /^(\d{1,2}):\d{2}\s*([ap])/i.exec(label.trim())
  if (!match) return 'Morning'

  const hour12 = Number(match[1])
  const pm = match[2]!.toLowerCase() === 'p'
  const hour = pm ? (hour12 === 12 ? 12 : hour12 + 12) : hour12 === 12 ? 0 : hour12

  if (hour < 12) return 'Morning'
  if (hour < 17) return 'Afternoon'
  return 'Evening'
}
